"use client";
import { cn } from "@/lib/utils";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslations } from "next-intl";
import React from "react";

// ============================================================================
// Constants
// ============================================================================

const CONTEXTS = [1, 2, 3] as const;
type ContextKey = (typeof CONTEXTS)[number];

const CONTEXT_LINES: Record<ContextKey, number[]> = {
  1: [1, 2, 3, 4],
  2: [1, 2, 3],
  3: [1, 2, 3],
};

const ROTATION_MS = 6500;
const TOKEN_PATTERN = /(\[[a-zA-Z]+\])/g;

// ============================================================================
// Root Section
// ============================================================================

export const DynamicScriptsSection = () => {
  return (
    <section className="dark relative w-full overflow-hidden bg-neutral-950">
      <SectionBackground />
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-20 md:py-28 lg:py-36">
        <BrandReveal />
        <div className="mt-12 lg:mt-20">
          <ScriptScene />
        </div>
        <div className="mt-14 lg:mt-24">
          <CapabilityGrid />
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// Background — dark band with hairlines
// ============================================================================

const SectionBackground = () => (
  <>
    <div className="absolute inset-x-0 top-0 h-px bg-neutral-800" />
    <div className="absolute inset-x-0 bottom-0 h-px bg-neutral-800" />
  </>
);

// ============================================================================
// Block 1 — Brand Reveal
// ============================================================================

const BrandReveal = () => {
  const t = useTranslations("dynamicScripts");
  return (
    <div className="flex flex-col items-center text-center">
      <p className="inline-flex items-center gap-1.5 text-sm font-semibold text-red-500">
        <ScriptIcon className="h-3.5 w-3.5" />
        Dynamic Scripts
      </p>

      <h2 className="mt-4 text-3xl md:text-5xl font-bold leading-tight tracking-tight text-white max-w-3xl">
        {t("tagline")}
      </h2>

      <p className="mt-5 text-sm md:text-base text-neutral-400 max-w-2xl leading-relaxed">
        {t("lead")}
      </p>
    </div>
  );
};

// ============================================================================
// Block 2 — Script Scene
// ============================================================================

const ScriptScene = () => {
  const t = useTranslations("dynamicScripts");
  const [active, setActive] = React.useState<ContextKey>(1);

  React.useEffect(() => {
    const id = setInterval(() => {
      setActive((prev) => {
        const idx = CONTEXTS.indexOf(prev);
        return CONTEXTS[(idx + 1) % CONTEXTS.length];
      });
    }, ROTATION_MS);
    return () => clearInterval(id);
  }, []);

  // Build the token map from i18n — re-resolves on locale change
  const tokens: Record<string, string> = {
    salutation: t("lead_salutation"),
    firstName: t("lead_first_name"),
    lastName: t("lead_last_name"),
    role: t("lead_role"),
    company: t("lead_company"),
    industry: t("lead_industry"),
    repFirstName: t("rep_first_name"),
    meetingTime: t("meeting_time"),
  };

  return (
    <div className="relative rounded-2xl border border-neutral-800 bg-neutral-950 overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-12 min-h-[560px]">
        <ContextRail active={active} onSelect={setActive} />
        <ScriptPanel active={active} tokens={tokens} />
      </div>
      <div className="border-t border-neutral-800 bg-neutral-900/40 px-5 py-3 flex items-center gap-2">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
        <span className="text-[10px] text-neutral-500">{t("footer_meta")}</span>
      </div>
    </div>
  );
};

// ----------------------------------------------------------------------------
// Context rail (left)
// ----------------------------------------------------------------------------

const ContextRail = ({
  active,
  onSelect,
}: {
  active: ContextKey;
  onSelect: (key: ContextKey) => void;
}) => {
  const t = useTranslations("dynamicScripts");
  return (
    <div className="lg:col-span-4 border-b lg:border-b-0 lg:border-r border-neutral-800 bg-neutral-900/40 p-5 flex flex-col">
      {/* Lead chip */}
      <div className="rounded-xl border border-neutral-800 bg-neutral-900 p-3 mb-5">
        <div className="text-[10px] font-semibold text-neutral-500 uppercase tracking-wider mb-2">
          {t("lead_panel_label")}
        </div>
        <div className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-red-500/15 text-red-300 text-[11px] font-semibold ring-1 ring-red-500/30">
            {t("lead_first_name")[0]}
            {t("lead_last_name")[0]}
          </span>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-neutral-100 truncate">
              {t("lead_first_name")} {t("lead_last_name")}
            </div>
            <div className="text-[11px] text-neutral-500 truncate">
              {t("lead_role")} · {t("lead_company")}
            </div>
          </div>
        </div>
      </div>

      {/* Context label */}
      <div className="flex items-center gap-1.5 mb-3">
        <BoltIcon className="h-3.5 w-3.5 text-red-500" />
        <span className="text-[10px] font-semibold tracking-wider text-red-400 uppercase">
          {t("context_label")}
        </span>
      </div>
      <div className="text-[10px] text-neutral-500 mb-3">{t("context_meta")}</div>

      {/* Context list */}
      <ul className="space-y-2 flex-1">
        {CONTEXTS.map((key) => {
          const isActive = key === active;
          return (
            <li key={key}>
              <button
                type="button"
                onClick={() => onSelect(key)}
                className={cn(
                  "group w-full text-left rounded-xl border p-3 transition-all",
                  isActive
                    ? "border-red-500/50 bg-neutral-900"
                    : "border-neutral-800 bg-neutral-900/40 hover:border-neutral-700 hover:bg-neutral-900/60"
                )}
              >
                <div className="flex items-center gap-3">
                  <span
                    className={cn(
                      "flex h-3 w-3 flex-shrink-0 items-center justify-center rounded-full",
                      isActive
                        ? "bg-red-500"
                        : "bg-neutral-700"
                    )}
                  >
                  </span>
                  <div className="min-w-0 flex-1">
                    <div
                      className={cn(
                        "text-sm font-semibold truncate",
                        isActive ? "text-white" : "text-neutral-300"
                      )}
                    >
                      {t(`ctx_${key}_label`)}
                    </div>
                    <div className="text-[11px] text-neutral-500 truncate">
                      {t(`ctx_${key}_subtitle`)}
                    </div>
                  </div>
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

// ----------------------------------------------------------------------------
// Script panel (right)
// ----------------------------------------------------------------------------

const ScriptPanel = ({
  active,
  tokens,
}: {
  active: ContextKey;
  tokens: Record<string, string>;
}) => {
  const t = useTranslations("dynamicScripts");
  const lines = CONTEXT_LINES[active];

  return (
    <div className="lg:col-span-8 relative overflow-hidden p-5 bg-neutral-950 flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-1.5">
          <FlameIcon className="h-3.5 w-3.5 text-red-500" />
          <span className="text-[10px] font-semibold tracking-wider text-red-400 uppercase">
            {t(`ctx_${active}_label`)}
          </span>
        </div>
        <span className="text-[10px] text-neutral-500">
          {t("script_panel_meta")}
        </span>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={active}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="rounded-xl border border-neutral-800 bg-neutral-900 p-5 flex-1 relative overflow-hidden"
        >
          <div className="absolute inset-x-0 -top-px h-px bg-neutral-800" />
          <div className="text-sm font-semibold text-white mb-4">
            {t(`script_${active}_title`)}
          </div>
          <div className="space-y-3.5 text-[15px] text-neutral-200 leading-relaxed">
            {lines.map((lineNumber, i) => (
              <ScriptLine
                key={`${active}-${lineNumber}`}
                rawText={t(`script_${active}_line_${lineNumber}`)}
                tokens={tokens}
                index={i}
              />
            ))}
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Custom-field bar */}
      <div className="mt-5">
        <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-medium mb-2">
          {t("fields_label")}
        </div>
        <FieldChips tokens={tokens} />
      </div>
    </div>
  );
};

const ScriptLine = ({
  rawText,
  tokens,
  index,
}: {
  rawText: string;
  tokens: Record<string, string>;
  index: number;
}) => {
  const parts = rawText.split(TOKEN_PATTERN).filter(Boolean);
  return (
    <motion.p
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3, delay: 0.1 + index * 0.08 }}
    >
      {parts.map((part, i) => {
        if (part.startsWith("[") && part.endsWith("]")) {
          const tokenName = part.slice(1, -1);
          const value = tokens[tokenName] ?? part;
          return (
            <span
              key={i}
              className="inline-flex items-baseline rounded px-1.5 py-0.5 mx-0.5 bg-red-500/15 text-red-200 ring-1 ring-red-500/30 font-medium"
              title={`{${tokenName}}`}
            >
              {value}
            </span>
          );
        }
        return <React.Fragment key={i}>{part}</React.Fragment>;
      })}
    </motion.p>
  );
};

const FieldChips = ({ tokens }: { tokens: Record<string, string> }) => {
  const labels: Array<[string, string]> = [
    ["salutation", "salutation"],
    ["company", "company"],
    ["role", "role"],
    ["industry", "industry"],
    ["meetingTime", "meetingTime"],
  ];
  return (
    <div className="flex flex-wrap gap-1.5">
      {labels.map(([key, label]) => (
        <span
          key={key}
          className="inline-flex items-center gap-1.5 rounded border border-neutral-800 bg-neutral-900/70 px-2 py-1 text-[11px] text-neutral-400"
        >
          <span className="text-neutral-600 font-mono">[{label}]</span>
          <span className="text-neutral-300 font-medium">→</span>
          <span className="text-red-300 font-medium">{tokens[key]}</span>
        </span>
      ))}
    </div>
  );
};

// ============================================================================
// Block 3 — Capability Grid
// ============================================================================

type TileConfig = { key: string; icon: React.ReactNode };

const TILES: TileConfig[] = [
  { key: "t1", icon: <IconAutoSwitch /> },
  { key: "t2", icon: <IconTokens /> },
  { key: "t3", icon: <IconLibrary /> },
  { key: "t4", icon: <IconNote /> },
];

const CapabilityGrid = () => {
  const t = useTranslations("dynamicScripts");
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {TILES.map((tile) => (
        <CapabilityTile
          key={tile.key}
          icon={tile.icon}
          label={t(`${tile.key}_label`)}
          desc={t(`${tile.key}_desc`)}
        />
      ))}
    </div>
  );
};

const CapabilityTile = ({
  icon,
  label,
  desc,
}: {
  icon: React.ReactNode;
  label: string;
  desc: string;
}) => (
  <div className="relative rounded-xl border border-neutral-800 bg-neutral-900/50 p-5">
    <div className="relative">
      <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-neutral-800 text-red-500">
        {icon}
      </div>
      <h3 className="mt-3 text-sm md:text-base font-semibold text-white">
        {label}
      </h3>
      <p className="mt-1 text-xs md:text-sm text-neutral-400 leading-relaxed">
        {desc}
      </p>
    </div>
  </div>
);

// ============================================================================
// Icons
// ============================================================================

function FlameIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden
    >
      <path d="M12 2.5c1.3 3.1 3.8 4.6 4.7 6.9.9 2.3.3 4.7-1.4 6.4-.6.6-1.3 1-2.1 1.2.5-.9.6-2 .2-3-.5-1.2-1.7-1.9-2.9-1.9-.3 1-.9 1.9-1.7 2.5-1.7 1.3-2.3 3.5-1.6 5.4.5 1.4 1.6 2.6 3 3-3.7-.2-7-3.1-7.4-6.9-.4-4.1 2.4-7.7 5.5-10.1C10.3 4.7 11.4 3.6 12 2.5z" />
    </svg>
  );
}

function BoltIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden
    >
      <path d="M13 2L4 14h6l-1 8 9-12h-6l1-8z" />
    </svg>
  );
}

function ScriptIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <path d="M6 3h11a2 2 0 0 1 2 2v13a3 3 0 0 0 3 3H8a3 3 0 0 1-3-3V5a2 2 0 0 1 1-2z" />
      <path d="M9 8h7M9 12h7M9 16h4" />
    </svg>
  );
}

function IconAutoSwitch() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden
    >
      <path d="M3 8h13l-3-3M21 16H8l3 3" />
    </svg>
  );
}

function IconTokens() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden
    >
      <rect x="3" y="6" width="7" height="5" rx="1" />
      <rect x="14" y="6" width="7" height="5" rx="1" />
      <rect x="3" y="13" width="7" height="5" rx="1" />
      <rect x="14" y="13" width="7" height="5" rx="1" />
    </svg>
  );
}

function IconLibrary() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden
    >
      <rect x="4" y="4" width="4" height="16" rx="1" />
      <rect x="10" y="4" width="4" height="16" rx="1" />
      <path d="m17 5 3 16" />
    </svg>
  );
}

function IconNote() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden
    >
      <path d="M5 4a2 2 0 0 1 2-2h8l5 5v13a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2z" />
      <path d="M14 2v5a2 2 0 0 0 2 2h4" />
      <path d="M9 13h6M9 17h4" />
    </svg>
  );
}
